import React, { useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { DropdownCaret } from 'components/atoms/DropdownCaret';
import { DayPickerPopover } from 'components/molecules/DayPickerPopover';

import { parseYmd, safeLocale, toYmd } from './scheduleViewUtils';

interface ScheduleDatePickerProps {
  readonly selectedDate: string;
  readonly locale: string;
  readonly onSelectDate: (date: string) => void;
}

/**
 * Single-date calendar picker that anchors the schedule (replaces the old Today/Tomorrow/anchor
 * row). The trigger shows the selected date's month — plus the year when it isn't the current one,
 * and the day on mobile (where the date strip is hidden). Past dates are disabled; picking a date
 * jumps the schedule to it (and the strip auto-centres on the selection).
 */
const ScheduleDatePicker = ({
  selectedDate,
  locale,
  onSelectDate,
}: ScheduleDatePickerProps) => {
  const { t: translate } = useTranslation();
  const [open, setOpen] = useState(false);

  const selected = parseYmd(selectedDate);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  const monthText = new Intl.DateTimeFormat(safeLocale(locale), {
    month: 'long',
    ...(selected.getFullYear() !== now.getFullYear() && { year: 'numeric' }),
  }).format(selected);

  const handlePick = (date: Date) => {
    onSelectDate(toYmd(date));
    setOpen(false);
  };

  const trigger = (
    <PickerTrigger
      type="button"
      className="schedule-view__date-picker"
      aria-label={translate('pickADate', { defaultValue: 'Pick a date' })}
      aria-haspopup="dialog"
      aria-expanded={open}
    >
      <span className="schedule-view__date-picker-month">{monthText}</span>
      {/* Day is shown only on mobile, where the date strip is hidden (e.g. "June 19"). */}
      <span className="schedule-view__date-picker-day">
        {' '}
        {selected.getDate()}
      </span>
      <DropdownCaret className="schedule-view__date-picker-caret" open={open} />
    </PickerTrigger>
  );

  return (
    <DayPickerPopover
      mode="single"
      required
      asChild
      classNamePrefix="schedule-view__"
      open={open}
      onOpenChange={setOpen}
      defaultMonth={selected}
      selected={selected}
      onSelect={handlePick}
      disabled={{ before: today }}
      trigger={trigger}
      footer={null}
    />
  );
};

export default ScheduleDatePicker;

/* Calendar-picker trigger that replaces the old anchor row ("June ▾"; "June 19 ▾" on mobile). */
const PickerTrigger = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex: 0 0 auto;
  background: none;
  border: none;
  cursor: pointer;
  font-family: inherit;
  font-size: 14px;
  color: var(--way-colors-primaryTextColor);
  white-space: nowrap;

  .schedule-view__date-picker-day {
    display: none;
  }

  @media all and (min-width: 768px) {
    font-size: 15px;
  }

  /* Mobile: show the day next to the month (e.g. "June 19"), since the strip is hidden. */
  @media all and (max-width: 767px) {
    .schedule-view__date-picker-day {
      display: inline;
    }
  }
`;
